import React, { useState, useRef, useEffect } from 'react';
import { User, Database, Settings, LogOut, Users, X, ArrowRightLeft, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { databasesApi } from '../../services/api';
import { isEntraAuth } from '../../auth/authConfig';
import { msalInstance } from '../../auth/authConfig';
import logo from '../../assets/logos/WM-Logo-CB-White-Header.png';

const Header = ({ onNavigateHome, setCurrentPage }) => {
  const navigate = useNavigate();
  const { currentUser, isImpersonating, hasPersistedIdentity, users, isAdmin, impersonate, stopImpersonating, claimIdentity } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showDbSwitcherModal, setShowDbSwitcherModal] = useState(false);
  // Local DB list for the switcher modal — fetched independently so it works on admin pages too
  const [switcherDatabases, setSwitcherDatabases] = useState([]);

  const userDropdownRef = useRef(null);
  const impersonateRef = useRef(null);
  const dbSwitcherRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (impersonateRef.current && !impersonateRef.current.contains(event.target)) {
        setShowImpersonateModal(false);
      }
      if (dbSwitcherRef.current && !dbSwitcherRef.current.contains(event.target)) {
        setShowDbSwitcherModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePreferences = () => {
    setShowUserDropdown(false);
    setCurrentPage('preferences');
  };

  const handleSignOut = () => {
    setShowUserDropdown(false);
    if (isEntraAuth() && msalInstance) {
      msalInstance.logoutRedirect();
    } else {
      console.log('Sign out clicked');
    }
  };

  const handleImpersonate = () => {
    setShowUserDropdown(false);
    setShowImpersonateModal(true);
  };

  const handleSwitchDatabase = () => {
    setShowUserDropdown(false);
    // Fetch databases fresh for the modal (works on both data and admin pages)
    databasesApi.getAvailable()
      .then(res => setSwitcherDatabases(res.data || []))
      .catch(() => setSwitcherDatabases([]));
    setShowDbSwitcherModal(true);
  };

  const handleSelectUser = (user) => {
    impersonate(user);
    setShowImpersonateModal(false);
  };

  const handleSelectDatabase = (db) => {
    const displayName = db.displayName || db.name;
    const slug = displayName.toLowerCase();
    const dbName = db.databaseName || db.id;
    // Update localStorage
    localStorage.setItem('pmd-selected-database', dbName);
    localStorage.setItem('pmd-selected-db-slug', slug);
    window.dispatchEvent(new Event('pmd-database-changed'));
    // Navigate to the new DB home
    navigate(`/${slug}/`);
    setShowDbSwitcherModal(false);
  };

  // Sort users: current user first, then alphabetically
  const sortedUsers = [...users].sort((a, b) => {
    if (a.id === currentUser?.id) return -1;
    if (b.id === currentUser?.id) return 1;
    return (a.user_Name || '').localeCompare(b.user_Name || '');
  });

  return (
    <header className="header">
      <div className="header-left">
        <img
          src={logo}
          alt="WM Logo"
          className="header-logo"
          onClick={onNavigateHome}
        />
        <span className="header-title">DAWN</span>
      </div>
      <div className="header-right">
        <div className="header-user-info">
          {isImpersonating ? (
            <div className="header-impersonation-area">
              <div className="header-impersonation-icons">
                {/* Green check — "This is me" (claim identity, persists) */}
                {(!isEntraAuth()) && (
                  <button
                    className="header-imp-btn header-imp-claim"
                    onClick={() => claimIdentity()}
                    title="This is me — remember for next session"
                  >
                    <Check size={13} />
                  </button>
                )}
                {/* Red X — "Go back" (revert to identity) */}
                <button
                  className="header-imp-btn header-imp-revert"
                  onClick={stopImpersonating}
                  title="Stop impersonating"
                >
                  <X size={13} />
                </button>
              </div>
              <div className="header-impersonation-text">
                <span className="header-user-name" style={{ color: '#fca5a5', fontWeight: 600 }}>
                  {currentUser?.user_Name || 'Unknown'}
                </span>
                <span className="header-user-group" style={{ color: '#fca5a5' }}>
                  {currentUser?.access_Group || ''}
                </span>
              </div>
            </div>
          ) : (
            <>
              <span className="header-user-name">{currentUser?.user_Name || 'Loading...'}</span>
              <span className="header-user-group">{currentUser?.access_Group || ''}</span>
            </>
          )}
        </div>
        <div className="header-user-menu" ref={userDropdownRef}>
          <button
            className="header-user-icon"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            <User size={24} />
          </button>
          {showUserDropdown && (
            <div className="header-user-dropdown">
              <button className="header-user-option" onClick={handleSwitchDatabase}>
                <ArrowRightLeft size={16} />
                <span>Switch Database</span>
              </button>
              <div className="header-dropdown-divider" />
              {(!isEntraAuth() || isAdmin) && (
                <>
                  <button className="header-user-option" onClick={handleImpersonate}>
                    <Users size={16} />
                    <span>Impersonate User</span>
                  </button>
                  <div className="header-dropdown-divider" />
                </>
              )}
              <button className="header-user-option" onClick={handlePreferences}>
                <Settings size={16} />
                <span>Preferences</span>
              </button>
              <div className="header-dropdown-divider" />
              <button className="header-user-option" onClick={handleSignOut}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Impersonate Modal */}
      {showImpersonateModal && (
        <div className="impersonate-overlay">
          <div className="impersonate-modal" ref={impersonateRef}>
            <div className="impersonate-header">
              <h3>Impersonate User</h3>
              <button
                className="impersonate-close"
                onClick={() => setShowImpersonateModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="impersonate-list">
              {sortedUsers.map(user => (
                <button
                  key={user.id}
                  className={`impersonate-user ${user.id === currentUser?.id ? 'current' : ''}`}
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="impersonate-user-info">
                    <span className="impersonate-user-name">{user.user_Name}</span>
                    <span className="impersonate-user-group">{user.access_Group}</span>
                  </div>
                  {user.id === currentUser?.id && (
                    <span className="impersonate-current-badge">Current</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Database Switcher Modal */}
      {showDbSwitcherModal && (
        <div className="impersonate-overlay">
          <div className="db-switcher-modal" ref={dbSwitcherRef}>
            <div className="db-switcher-header">
              <h3>Switch Database</h3>
              <button
                className="impersonate-close"
                onClick={() => setShowDbSwitcherModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="db-switcher-list">
              {switcherDatabases.map(db => {
                const name = db.displayName || db.name;
                const isActive = name?.toLowerCase() === dbSlug;
                return (
                  <button
                    key={db.id || db.databaseName}
                    className={`db-switcher-row ${isActive ? 'current' : ''}`}
                    onClick={() => handleSelectDatabase(db)}
                  >
                    <div className="db-switcher-logo">
                      {db.iconUrl ? (
                        <img src={db.iconUrl} alt="" />
                      ) : (
                        <div className="db-switcher-logo-placeholder">
                          <Database size={16} />
                        </div>
                      )}
                    </div>
                    <div className="db-switcher-info">
                      <span className="db-switcher-name">{name}</span>
                      {db.description && (
                        <span className="db-switcher-desc">{db.description}</span>
                      )}
                    </div>
                    {isActive && (
                      <span className="db-switcher-badge">Current</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
